/**
 * Dev Tools Component
 *
 * Provides multi-service API testing capabilities with real-time validation,
 * dynamic form generation, and comprehensive response viewing.
 *
 * @component
 * @example
 * // Usage in routing
 * <Route path="/dev-tools" component={SupportServiceForm} />
 */
import React, { useCallback, useContext, useEffect, useState } from "react";

import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { supportServiceConnectClient } from "api";
import { useLocation, useParams } from "react-router-dom";

import { SupportService } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_connect";

import { EnhancedResponseViewer } from "./components/enhanced-response-viewer";
import { DevToolsErrorBoundary } from "./components/error-boundary";
import { FormFieldRenderer } from "./components/form-field-renderer";
import { MethodSelector } from "./components/method-selector";
import { ServiceSelector } from "./components/service-selector";
import { ValidationStatus } from "./components/validation-status";
import { MethodNames } from "./constants";
import { MethodExecutionService, SchemaService } from "./services";
import { FormState, MethodDefinition, ServiceInfo } from "./types";
import {
  buildRequestObject,
  createEmptyFormState,
  prefillFormWithOrgData,
  updateFormField,
  validateField,
} from "./utils/form-utils";
import { getOrgWithSubscription } from "api/endpoints/organisation";
import { callOptionsHeaders } from "api/helpers";
import { OrgDataContext } from "context/organisation-context";

type LocationState = {
  orgName: string;
};

type UrlParamTypes = {
  regionCode: string;
  roleId: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      style={{ textAlign: "left" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2, textAlign: "left" }}>{children}</Box>
      )}
    </div>
  );
}

const SupportServiceForm: React.FC = () => {
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const location = useLocation<LocationState>();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { orgName } = location.state || { orgName: "" };

  // State management
  const [availableServices, setAvailableServices] = useState<ServiceInfo[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>("support");
  const [methods, setMethods] = useState<MethodDefinition[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<MethodNames | "">("");
  const [formState, setFormState] = useState<FormState>({
    data: {},
    errors: {},
    touched: {},
    isValid: false,
    isSubmitting: false,
  });
  const [response, setResponse] = useState<unknown>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [isLoadingOrgData, setIsLoadingOrgData] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  // Load available services
  useEffect(() => {
    try {
      const services = SchemaService.getAvailableServices();
      setAvailableServices(services);
    } catch (error) {
      console.error("Failed to load available services:", error);
    }
  }, []);

  // Load methods when service changes
  useEffect(() => {
    const loadMethods = async () => {
      try {
        const methodDefinitions =
          await SchemaService.getMethodDefinitionsForService(selectedServiceId);
        setMethods(methodDefinitions);
        // Reset selected method when service changes
        setSelectedMethod("");
        setResponse(null);
      } catch (error) {
        console.error(
          `Failed to load methods for service ${selectedServiceId}:`,
          error
        );
        setMethods([]);
      }
    };

    if (selectedServiceId) {
      loadMethods();
    }
  }, [selectedServiceId]);

  // Fetch organization data if needed
  useEffect(() => {
    if (!orgData?.id && roleId && regionCode && !isLoadingOrgData) {
      setIsLoadingOrgData(true);
      getOrgWithSubscription({ roleId }, callOptionsHeaders(regionCode, roleId))
        .then((response) => {
          if (response.role) {
            setOrgData(response.role);
          }
        })
        .catch((error) => {
          console.error("Failed to load org data:", error);
        })
        .finally(() => {
          setIsLoadingOrgData(false);
        });
    }
  }, [orgData, roleId, regionCode, isLoadingOrgData, setOrgData]);

  // Prefill form when method or orgData changes
  useEffect(() => {
    if (selectedMethod && methods.length > 0) {
      const method = methods.find((m) => m.name === selectedMethod);
      if (method) {
        let initialState = createEmptyFormState();

        const effectiveOrgData = orgData?.id
          ? orgData
          : roleId
          ? { ...orgData, id: roleId }
          : null;

        if (effectiveOrgData?.id && method.inputSchema) {
          try {
            initialState = prefillFormWithOrgData(
              initialState,
              effectiveOrgData,
              method.inputSchema
            );
          } catch (error) {
            console.error("Error during prefill:", error);
          }
        }

        setFormState(initialState);
      }
    }
  }, [selectedMethod, methods, orgData, roleId]);

  // Event handlers with enhanced error handling
  const handleInputChange = useCallback(
    (fieldName: string, value: unknown) => {
      setFormState((prevState) => {
        try {
          const updatedState = updateFormField(prevState, fieldName, value);

          // Run validation if validation is enabled and field has been touched
          if (showValidation) {
            const selectedMethodDef = methods.find(
              (method) => method.name === selectedMethod
            );

            if (selectedMethodDef?.inputSchema) {
              const fieldSchema = selectedMethodDef.inputSchema.find(
                (field) => field.name === fieldName
              );

              if (fieldSchema) {
                const error = validateField(fieldSchema, value);
                const newErrors = { ...updatedState.errors };

                if (error) {
                  newErrors[fieldName] = error;
                } else {
                  delete newErrors[fieldName];
                }

                return {
                  ...updatedState,
                  errors: newErrors,
                  isValid: Object.keys(newErrors).length === 0,
                };
              }
            }
          }

          return updatedState;
        } catch (error) {
          console.error("Error in handleInputChange:", error);
          return prevState; // Return previous state on error to prevent crashes
        }
      });
    },
    [showValidation, methods, selectedMethod]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMethod) return;

    setFormState((prev) => ({ ...prev, isSubmitting: true }));
    setShowValidation(true);

    try {
      // Build clean request object from form data
      const requestObject = buildRequestObject(formState.data);

      const method = methods.find((m) => m.name === selectedMethod);
      if (method) {
        const result = await MethodExecutionService.executeMethodForService(
          selectedServiceId,
          selectedMethod,
          requestObject
        );
        setResponse(result);
        // Switch to Response tab after successful execution
        setActiveTab(1);
      } else {
        // Fallback to legacy method with better error handling
        const legacyMethod =
          SupportService.methods[
            selectedMethod as keyof typeof SupportService.methods
          ];
        if (legacyMethod?.I) {
          const request = new legacyMethod.I(requestObject);
          const response = await (
            supportServiceConnectClient as unknown as Record<
              string,
              (req: unknown) => Promise<unknown>
            >
          )[selectedMethod](request);
          setResponse(response);
          // Switch to Response tab after successful execution
          setActiveTab(1);
        } else {
          throw new Error(
            `Method ${selectedMethod} not found or improperly configured`
          );
        }
      }
    } catch (error) {
      console.error("Method execution error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setResponse({
        error: errorMessage,
        details: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      });
      // Switch to Response tab even on error to show the error
      setActiveTab(1);
    } finally {
      setFormState((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleCopyRequest = async () => {
    try {
      const requestObject = buildRequestObject(formState.data);
      const requestJson = JSON.stringify(requestObject, null, 2);
      await navigator.clipboard.writeText(requestJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const renderMethodInputs = () => {
    const selectedMethodDef = methods.find(
      (method) => method.name === selectedMethod
    );
    if (!selectedMethodDef?.inputSchema) return null;

    return (
      <FormFieldRenderer
        fields={selectedMethodDef.inputSchema}
        formState={formState}
        onFieldChange={handleInputChange}
      />
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} style={{ paddingBottom: "16px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <Grid item xs={11}>
            <Typography
              variant="h5"
              color="textPrimary"
              gutterBottom
              data-testid="organisation-dev-tools-title"
            >
              {orgName && `${orgName} - `}
              Dev Tools
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ paddingBottom: "16px" }}>
        <form onSubmit={handleSubmit}>
          {/* Service and Method Selection */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "flex-start",
              // Keep row layout for compact display
              flexDirection: "row",
            }}
          >
            <ServiceSelector
              selectedServiceId={selectedServiceId}
              onServiceChange={setSelectedServiceId}
              services={availableServices}
            />

            <MethodSelector
              methods={methods}
              selectedMethod={selectedMethod}
              onMethodChange={(methodName) =>
                setSelectedMethod(methodName as MethodNames)
              }
            />
          </Box>

          {/* Side-by-side layout for Form and Results */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Left side - Form Input */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  padding: 2,
                  minHeight: 400,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ mb: 1 }}>
                  {isLoadingOrgData ? (
                    <Box sx={{ py: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Loading organization data...
                      </Typography>
                    </Box>
                  ) : (
                    renderMethodInputs()
                  )}
                </Box>

                {selectedMethod && (
                  <ValidationStatus
                    validationEnabled={showValidation}
                    errorCount={Object.keys(formState.errors).length}
                    onToggle={() => {
                      try {
                        const newValidationState = !showValidation;
                        setShowValidation(newValidationState);

                        // If turning validation on, validate all touched fields
                        if (newValidationState) {
                          const selectedMethodDef = methods.find(
                            (method) => method.name === selectedMethod
                          );

                          if (selectedMethodDef?.inputSchema) {
                            const newErrors: Record<string, string> = {};

                            selectedMethodDef.inputSchema.forEach(
                              (fieldSchema) => {
                                try {
                                  const fieldValue =
                                    formState.data[fieldSchema.name];
                                  const isTouched =
                                    formState.touched[fieldSchema.name];

                                  // Only validate touched fields
                                  if (isTouched) {
                                    const error = validateField(
                                      fieldSchema,
                                      fieldValue
                                    );
                                    if (error) {
                                      newErrors[fieldSchema.name] = error;
                                    }
                                  }
                                } catch (fieldError) {
                                  console.error(
                                    `Validation error for field ${fieldSchema.name}:`,
                                    fieldError
                                  );
                                  // Continue with other fields even if one fails
                                }
                              }
                            );

                            setFormState((prev) => ({
                              ...prev,
                              errors: newErrors,
                              isValid: Object.keys(newErrors).length === 0,
                            }));
                          }
                        } else {
                          // If turning validation off, clear all errors
                          setFormState((prev) => ({
                            ...prev,
                            errors: {},
                            isValid: true,
                          }));
                        }
                      } catch (error) {
                        console.error("Error toggling validation:", error);
                        // Fallback: just toggle the validation state without validation logic
                        setShowValidation(!showValidation);
                      }
                    }}
                    isValid={formState.isValid}
                  />
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formState.isSubmitting}
                  sx={{ mt: 1 }}
                >
                  {formState.isSubmitting ? "Submitting..." : "Execute Method"}
                </Button>
              </Box>
            </Grid>

            {/* Right side - Results */}
            <Grid item xs={12} md={7}>
              <Paper
                sx={{
                  padding: 2,
                  minHeight: 400,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    value={activeTab}
                    onChange={(event, newValue) => setActiveTab(newValue)}
                  >
                    {selectedMethod && <Tab label="Request Preview" />}
                    <Tab label="Response" />
                  </Tabs>
                </Box>

                <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                  {/* Request Preview Tab - Shows current form state */}
                  {selectedMethod && (
                    <TabPanel value={activeTab} index={0}>
                      <Box sx={{ textAlign: "left" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="subtitle1"
                              sx={{ mb: 0.5, fontWeight: 600 }}
                            >
                              Request Preview
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              JSON request that will be sent to the API
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            {(() => {
                              const requestObject = buildRequestObject(
                                formState.data
                              );
                              const fieldCount =
                                Object.keys(requestObject).length;
                              const requestSize =
                                fieldCount > 0
                                  ? new Blob([JSON.stringify(requestObject)])
                                      .size
                                  : 0;
                              const sizeLabel =
                                requestSize < 1024
                                  ? `${requestSize} B`
                                  : `${(requestSize / 1024).toFixed(1)} KB`;

                              return fieldCount > 0 ? (
                                <>
                                  <Chip
                                    label={`${fieldCount} field${
                                      fieldCount !== 1 ? "s" : ""
                                    }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={sizeLabel}
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                  <Tooltip
                                    title={
                                      copied ? "Copied!" : "Copy to clipboard"
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={handleCopyRequest}
                                      sx={{ ml: 0.5 }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : null;
                            })()}
                          </Box>
                        </Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "grey.900"
                                : "grey.50",
                            border: (theme) =>
                              `1px solid ${theme.palette.divider}`,
                            textAlign: "left",
                          }}
                        >
                          <pre
                            style={{
                              margin: 0,
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              textAlign: "left",
                              overflow: "auto",
                              color: "inherit",
                            }}
                          >
                            {(() => {
                              const requestObject = buildRequestObject(
                                formState.data
                              );
                              const hasData =
                                Object.keys(requestObject).length > 0;

                              return hasData
                                ? JSON.stringify(requestObject, null, 2)
                                : "// No data entered yet.\n// Fill out the form fields to see the request preview.";
                            })()}
                          </pre>
                        </Paper>
                      </Box>
                    </TabPanel>
                  )}

                  {/* Response Tab */}
                  <TabPanel value={activeTab} index={selectedMethod ? 1 : 0}>
                    {formState.isSubmitting ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Executing method...
                        </Typography>
                      </Box>
                    ) : response !== null ? (
                      <EnhancedResponseViewer response={response} />
                    ) : (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Execute a method to see the response here
                        </Typography>
                      </Box>
                    )}
                  </TabPanel>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

/**
 * Main Dev Tools Component with Error Boundary
 *
 * Wraps the core functionality in an error boundary for production safety
 */
const DevToolsWithErrorBoundary: React.FC = () => {
  return (
    <DevToolsErrorBoundary>
      <SupportServiceForm />
    </DevToolsErrorBoundary>
  );
};

export default DevToolsWithErrorBoundary;
